/**
 * OperationTitleDebtsModal — títulos de uma operação de crédito.
 *
 * Atende os DOIS botões da coluna Títulos, e a separação não é cosmética: pagar uma fatura de
 * garantia movimenta o mesmo dinheiro duas vezes (no título de garantia e nas parcelas, via
 * baldinho), então a garantia fica FORA dos agregados da operação — se ela aparecesse na mesma
 * lista, a contagem do botão discordaria do que a lista mostra e a soma pareceria o dobro.
 *
 *   nature 'DEBT'      → Parcelas/Recebíveis (INSTALLMENT,RECEIVABLE), contagem title_debts_count
 *   nature 'GUARANTEE' → Garantias, contagem guarantees_count
 */
import { useEffect, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import BadgeStatus from '../../../atoms/BadgeStatus';
import { listTitleDebts } from '../services/settlementApi';
import { businessReferenceLabels, businessReferenceLabelsPlural } from '../constants/settlement';
import { formatBRL } from '../../../../utils/currencyUtils';
import { formatDateBR } from '../../../../utils/DateUtils';

type BadgeColor = 'blue' | 'green' | 'red' | 'orange' | 'gray' | 'yellow' | 'purple' | 'indigo' | 'pink' | 'olive' | 'teal' | 'gold' | 'todo' | 'turquoise';

// Paleta de cobrança (ver settlement/constants/settlement.ts): pagamento é verde, o tom diz qual.
const statusColors: Record<string, BadgeColor> = {
    OPEN: 'gold',
    PAID: 'green',              // liquidado
    PARTIALLY_PAID: 'olive',    // pagamento parcial
    OVERPAID: 'teal',           // recebimento a maior
    SUPERSEDED: 'gray',
    CANCELLED: 'red',
};

const statusLabels: Record<string, string> = {
    OPEN: 'ABERTO',
    PAID: 'PAGO',
    PARTIALLY_PAID: 'PARCIALMENTE PAGO',
    SUPERSEDED: 'SUBSTITUÍDO',
    CANCELLED: 'CANCELADO',
    OVERPAID: 'PAGO EM EXCESSO',
};

const operationTypeLabels: Record<string, string> = {
    LOAN: 'Empréstimo',
    RECEIVABLES_ASSIGNMENT: 'Cessão de recebíveis',
    ACKNOWLEDGEMENT_OF_DEBT: 'Confissão de dívida',
    STRUCTURED_OPERATION: 'Operação estruturada',
};

const referenceLabel = (ref?: string) => businessReferenceLabelsPlural[ref || ''] || '-';

/** Natureza por extenso — usada onde é a NATUREZA que está sendo mostrada. */
const referenceColumn = (ref?: string) => businessReferenceLabels[ref || ''] || 'Parcela';

type Nature = 'DEBT' | 'GUARANTEE';

interface Props {
    operation: any;
    /** o que listar. Default 'DEBT' (parcelas/recebíveis) — garantia tem botão próprio. */
    nature?: Nature;
    onClose: () => void;
}

const OperationTitleDebtsModal: React.FC<Props> = ({ operation, nature = 'DEBT', onClose }) => {
    const isGuarantee = nature === 'GUARANTEE';
    // filtro explícito nos dois casos: a lista tem de casar com a contagem do botão
    const refFilter = isGuarantee ? 'GUARANTEE' : 'INSTALLMENT,RECEIVABLE';
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const data = await listTitleDebts({
                    'filter[credit_operation_id]': operation.credit_operation_id,
                    'filter[business_reference]': refFilter,
                    size: '200',
                    sort: 'due_date',
                });
                setRows(data.list || []);
            } catch { setRows([]); }
            setLoading(false);
        })();
    }, [operation.credit_operation_id, refFilter]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {isGuarantee ? 'Garantias' : 'Títulos de Dívida'} — {operation.contract_label || `${operation.contract_number || '?'}-${operation.amendment_number ?? 0}`}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="mb-4 grid grid-cols-4 gap-4 text-sm bg-gray-50 dark:bg-slate-700 p-3 rounded">
                    <div><span className="text-gray-500 dark:text-gray-400">Tipo:</span> <strong>{operationTypeLabels[operation.operation_type] || operation.operation_type || '-'}</strong></div>
                    {/* amount (soma dos títulos), não nominal_value (que vem do credit): é o
                        mesmo critério da coluna da tela, e é a soma do que esta lista mostra */}
                    <div><span className="text-gray-500 dark:text-gray-400">Valor Nominal:</span> <strong>{formatBRL(operation.amount)}</strong></div>
                    <div><span className="text-gray-500 dark:text-gray-400">$ Recebido:</span> <strong className="text-green-600">{formatBRL(operation.paid_amount)}</strong></div>
                    <div>
                        <span className="text-gray-500 dark:text-gray-400">
                            {isGuarantee ? 'Garantias' : referenceLabel(operation.business_reference)}:
                        </span>{' '}
                        <strong>{(isGuarantee ? operation.guarantees_count : operation.title_debts_count) ?? 0}</strong>
                    </div>
                </div>

                {isGuarantee && (
                    <div className="mb-4 px-3 py-2 text-xs bg-slate-100 dark:bg-slate-700 rounded">
                        Recebíveis dados em <strong>garantia</strong>. Pagar um deles liquida o próprio
                        título <strong>e</strong> amortiza as parcelas da operação — por isso o valor
                        deles não entra nos totais da operação, que contariam o mesmo dinheiro duas vezes.
                    </div>
                )}
                {loading ? (
                    <p className="text-center text-gray-500 py-4">Carregando...</p>
                ) : rows.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">
                        {isGuarantee ? 'Nenhuma garantia nesta operação' : 'Nenhum título encontrado'}
                    </p>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100 dark:bg-slate-700">
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 uppercase">
                                <th className="px-3 py-2">#</th>
                                <th className="px-3 py-2">Título/NF</th>
                                {/* "Nº" e não o nome da natureza: a coluna mostra o
                                    installment_number, não o business_reference */}
                                <th className="px-3 py-2 text-center">Nº</th>
                                <th className="px-3 py-2 text-right">Valor Nominal</th>
                                <th className="px-3 py-2 text-right">$ Recebido</th>
                                <th className="px-3 py-2 text-center">Vencimento</th>
                                <th className="px-3 py-2 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((td: any) => (
                                <tr key={td.id} className="border-b border-gray-100 dark:border-slate-700">
                                    <td className="px-3 py-2 text-xs font-mono text-gray-500" title={td.charge_id || ''}>
                                        {td.charge_id ? String(td.charge_id).slice(0, 8) : '-'}
                                    </td>
                                    {/* documento que originou a dívida — o charge_id do # não
                                        identifica nada para quem opera */}
                                    <td className="px-3 py-2 text-xs font-medium">{td.document_title || '-'}</td>
                                    <td className="px-3 py-2 text-center">{td.installment_number || '-'}</td>
                                    <td className="px-3 py-2 text-right">{formatBRL(td.amount)}</td>
                                    {/* Sem destaque de "recebeu demais" aqui de propósito: quem
                                        responde isso é a FRAÇÃO paga do charge, e ela não existe
                                        nesta listagem (que é de títulos, não de cobranças). Pago
                                        acima do amount, sozinho, é mora — marcar seria mentir. A
                                        tela de Títulos tem a fração e faz esse aviso. */}
                                    <td className="px-3 py-2 text-right">{formatBRL(td.paid_amount)}</td>
                                    <td className="px-3 py-2 text-center">{formatDateBR(td.due_date)}</td>
                                    <td className="px-3 py-2 text-center">
                                        <BadgeStatus text={statusLabels[td.status] || td.status} color={statusColors[td.status] || 'gray'} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default OperationTitleDebtsModal;
